import React, { useState, useEffect, useRef } from 'react';
import { X, Reply, ReplyAll, Forward, Folder, Trash2, Sparkles, AlertCircle, FileText, Download, Paperclip, Image, ImageOff, Mail, Shield, ShieldOff, ChevronDown, ChevronRight } from 'lucide-react';
import CaseFilingPanel from './CaseFilingPanel';
import ThreadView from './ThreadView';
import DeadlineBanner from './DeadlineBanner';
import SenderInfo from './SenderInfo';
import { formatDateTime } from '../../utils/dateUtils';
import { getEmailIntelligence } from '../../services/emailService';
import { getEmailAttachments, downloadAttachment } from '../../services/mailService';
import IntelligenceSidebar from './IntelligenceSidebar';

// CSS injected into HTML email iframes to style quoted content
const QUOTE_STYLES = `
blockquote, .gmail_quote, .yahoo_quoted, .moz-cite-prefix + blockquote,
div[class*="quote"], div[class*="Reply"], div[class*="reply"] {
  border-left: 3px solid #cbd5e1 !important;
  padding-left: 12px !important;
  margin: 12px 0 !important;
  color: #64748b !important;
}
.gmail_attr, .gmail_quote > div:first-child,
div[class*="wrote"], .moz-cite-prefix {
  color: #94a3b8 !important;
  font-size: 12px !important;
  margin-bottom: 4px !important;
}
blockquote blockquote, .gmail_quote .gmail_quote {
  border-left-color: #e2e8f0 !important;
  color: #94a3b8 !important;
}
`;

// Parse plain text email body into segments: original text vs quoted text
function parseEmailSegments(text) {
  if (!text) return [{ type: 'original', content: text }];
  
  const lines = text.split('\n');
  let quoteSplitIndex = -1;
  
  const quoteHeaderPatterns = [
    /^-{3,}\s*(Original|Forwarded)\s*(Message|message)\s*-{3,}/,
    /^_{3,}\s*$/,
    /^On\s+.{10,}\s+wrote:\s*$/,
    /^On\s+.{10,}\s+<.+@.+>\s+wrote:\s*$/,
    /^From:\s+.+$/,
    /^>{1,}\s*On\s+.+wrote:/,
    /^\*From:\*\s+.+$/,
  ];
  
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    
    for (const pattern of quoteHeaderPatterns) {
      if (pattern.test(trimmed)) {
        if (/^From:\s+.+$/.test(trimmed) || /^\*From:\*\s+.+$/.test(trimmed)) {
          const nextLines = lines.slice(i + 1, i + 5).map(l => l.trim());
          const hasEmailHeaders = nextLines.some(l => 
            /^(Sent|Date|To|Subject|Cc):\s+.+/.test(l) || /^\*(Sent|Date|To|Subject|Cc):\*\s+.+/.test(l)
          );
          if (!hasEmailHeaders) continue;
        }
        quoteSplitIndex = i;
        break;
      }
    }
    if (quoteSplitIndex >= 0) break;
    
    if (/^>/.test(trimmed) && i > 0) {
      let quoteCount = 0;
      for (let j = i; j < Math.min(i + 5, lines.length); j++) {
        if (/^>/.test(lines[j].trim()) || lines[j].trim() === '') quoteCount++;
      }
      if (quoteCount >= 3) {
        quoteSplitIndex = i;
        break;
      }
    }
  }
  
  if (quoteSplitIndex < 0) {
    return [{ type: 'original', content: text }];
  }
  
  const originalText = lines.slice(0, quoteSplitIndex).join('\n').replace(/\n{3,}$/g, '\n');
  const quotedText = lines.slice(quoteSplitIndex).join('\n');
  
  const result = [];
  if (originalText.trim()) result.push({ type: 'original', content: originalText });
  if (quotedText.trim()) result.push({ type: 'quoted', content: quotedText });
  
  return result.length > 0 ? result : [{ type: 'original', content: text }];
}

// Collapsible quoted content block for plain text emails
const QuotedBlock = ({ content, linkifyText }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="mt-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-400 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
      >
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {expanded ? 'Hide quoted text' : 'Show quoted text'}
        <span className="text-slate-400 ml-1">···</span>
      </button>
      {expanded && (
        <div className="mt-2 pl-3 border-l-[3px] border-slate-300 text-slate-500 text-sm whitespace-pre-wrap font-sans leading-relaxed">
          {linkifyText(content)}
        </div>
      )}
    </div>
  );
};

const EmailViewModal = ({ emailId, onClose, onFileToCase, onDelete, onReply, onReplyAll, onForward, inline = false, onMatterLinked, onDeadlineAccepted, onIntelligenceLoaded }) => {
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [showRemoteImages, setShowRemoteImages] = useState(false);
  const emailBodyRef = useRef(null);

  // Handle link clicks in email body
  useEffect(() => {
    const handleLinkClick = (e) => {
      const link = e.target.closest('a');
      if (link && link.href) {
        e.preventDefault();
        e.stopPropagation();
        window.open(link.href, '_blank', 'noopener,noreferrer');
      }
    };
    const bodyElement = emailBodyRef.current;
    if (bodyElement) {
      bodyElement.addEventListener('click', handleLinkClick);
      return () => bodyElement.removeEventListener('click', handleLinkClick);
    }
  }, [email]);

  const linkifyText = (text) => {
    if (!text) return text;
    const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, i) => {
      if (part.match(urlRegex)) {
        return (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline break-all"
            onClick={(e) => e.stopPropagation()}>
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return <Image className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const handleDownload = async (attachment) => {
    try {
      setDownloadingId(attachment.id);
      await downloadAttachment(emailId, attachment.id, attachment.filename);
    } catch {
      alert('Failed to download attachment');
    } finally { setDownloadingId(null); }
  };

  useEffect(() => {
    const fetchEmail = async () => {
      setLoading(true);
      setEmail(null);
      setAttachments([]);
      setShowRemoteImages(false);
      setError(null);
      try {
        const data = await getEmailIntelligence(emailId);
        setEmail(data);
        setLoading(false);
        if (data.intelligence) {
          onIntelligenceLoaded?.(data.intelligence);
        }
        if (data.has_attachments) {
          setLoadingAttachments(true);
          try {
            const attachData = await getEmailAttachments(emailId);
            setAttachments(attachData.attachments || []);
          } catch { /* ignore */ }
          finally { setLoadingAttachments(false); }
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load email');
        setLoading(false);
      }
    };
    if (emailId) fetchEmail();
  }, [emailId]);

  // Build iframe srcDoc with quote styling
  const buildHtmlSrcDoc = (body) => {
    let processedBody = body;
    if (!showRemoteImages) {
      processedBody = processedBody.replace(/<img[^>]+src=["']https?:\/\/[^"']+["'][^>]*\/?>/gi, '');
    }
    const trimmed = processedBody.trim().toLowerCase();
    const isFullHtmlDoc = trimmed.startsWith('<!doctype') || trimmed.startsWith('<html') || trimmed.startsWith('<?xml');
    
    if (isFullHtmlDoc) {
      const styleTag = `<style>img{max-width:100%!important;height:auto!important;}${QUOTE_STYLES}</style>`;
      if (processedBody.toLowerCase().includes('</head>')) {
        return processedBody.replace(/<\/head>/i, styleTag + '</head>');
      }
      return styleTag + processedBody;
    }
    
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
body{font-family:Arial,sans-serif;font-size:14px;line-height:1.6;color:#333;margin:16px;background:#fff;}
img{max-width:100%;height:auto;}p{margin-bottom:0.5em;}a{color:#2563eb;}
${QUOTE_STYLES}
</style></head><body>${processedBody}</body></html>`;
  };

  if (!emailId) return null;

  const urgencyColors = {
    critical: 'text-red-500 bg-red-500/10',
    high: 'text-orange-500 bg-orange-500/10',
    normal: 'text-blue-500 bg-blue-500/10',
    low: 'text-gray-500 bg-gray-500/10'
  };

  const typeIcons = {
    court_filing: '\u2696\ufe0f', client_inquiry: '\ud83d\udc64',
    opposing_counsel: '\u2694\ufe0f', vendor: '\ud83d\udce6', other: '\ud83d\udce7'
  };

  // ---- Shared inner content ----
  const headerContent = (
    <div className={`${inline ? 'px-4 py-3' : 'bg-gradient-to-r from-slate-800 to-slate-900/30 px-6 py-4'} border-b border-slate-700 flex items-center justify-between flex-shrink-0`}>
      <div className="flex items-center space-x-3">
        {inline ? (
          <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center">
            <Mail className="w-4 h-4 text-slate-300" />
          </div>
        ) : (
          <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-cyan-400" />
          </div>
        )}
        <div>
          <h2 className={`${inline ? 'text-base' : 'text-lg'} font-semibold text-white`}>Email Details</h2>
          {!inline && <p className="text-sm text-slate-400">AI-Enhanced View</p>}
        </div>
      </div>
      <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg transition-colors">
        <X className="w-5 h-5 text-slate-400" />
      </button>
    </div>
  );

  const bodyContent = (
    <div className={`${inline ? 'flex-1 overflow-y-auto min-h-0' : 'overflow-y-auto max-h-[calc(90vh-180px)]'}`}>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      ) : error ? (
        <div className="p-6">
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 font-medium">Error Loading Email</p>
              <p className="text-red-300/70 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-6 space-y-6">
          {/* AI Intelligence Banner */}
          {email.intelligence && (email.intelligence.urgency || email.intelligence.email_type) && (
            <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                <span className="text-sm font-medium text-cyan-400">AI Analysis</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {email.intelligence.urgency && (
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${urgencyColors[email.intelligence.urgency]}`}>
                    {email.intelligence.urgency.toUpperCase()}
                  </span>
                )}
                {email.intelligence.email_type && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-700 text-slate-300">
                    {typeIcons[email.intelligence.email_type]} {email.intelligence.email_type.replace('_', ' ')}
                  </span>
                )}
                {email.intelligence.confidence_score && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-700 text-slate-300">
                    {Math.round(email.intelligence.confidence_score * 100)}% confidence
                  </span>
                )}
              </div>
              {email.intelligence.ai_summary && (
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">{email.intelligence.ai_summary}</p>
              )}
            </div>
          )}

          {/* Case Filing Panel */}
          {email.intelligence && email.intelligence.matched_case_id && (
            <CaseFilingPanel
              emailId={emailId}
              intelligence={email.intelligence}
              onUpdate={async () => {
                try { const data = await getEmailIntelligence(emailId); setEmail(data); } catch {}
              }}
            />
          )}

          {/* Deadline Banner */}
          {email.intelligence && email.intelligence.extracted_deadlines && (
            <DeadlineBanner
              emailId={emailId}
              deadlines={email.intelligence.extracted_deadlines}
              caseId={email.case_id || email.intelligence?.matched_case_id}
            />
          )}

          {/* Email Header */}
          <div className="space-y-3">
            <h3 className="text-2xl font-semibold text-white">{email.subject}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-start">
                <span className="text-slate-500 w-16">From:</span>
                <span className="text-slate-300">{email.from}</span>
              </div>
              <div className="flex items-start">
                <span className="text-slate-500 w-16">To:</span>
                <span className="text-slate-300">{email.to}</span>
              </div>
              {email.cc && (
                <div className="flex items-start">
                  <span className="text-slate-500 w-16">CC:</span>
                  <span className="text-slate-300">{email.cc}</span>
                </div>
              )}
              <div className="flex items-start">
                <span className="text-slate-500 w-16">Date:</span>
                <span className="text-slate-300">{formatDateTime(email.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Sender Contact Info */}
          {email.from && <SenderInfo emailAddress={email.from} suggestedContactName={email.ai_suggested_contact_name} />}

          {/* Email Body */}
          <div ref={emailBodyRef} className="bg-white rounded-lg border border-slate-700 overflow-hidden">
            {email.body?.includes('<') && email.body?.includes('>') ? (
              <>
                {(email.body.match(/src=["']https?:\/\//gi) || []).length > 0 && !showRemoteImages && (
                  <div className="flex items-center justify-between bg-amber-500/10 border-b border-amber-500/30 px-4 py-2">
                    <div className="flex items-center gap-2 text-amber-400 text-sm">
                      <Shield className="w-4 h-4" />
                      <span>Remote images are blocked for privacy</span>
                    </div>
                    <button onClick={() => setShowRemoteImages(true)}
                      className="flex items-center gap-1.5 px-3 py-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded text-xs font-medium transition-colors">
                      <Image className="w-3 h-3" /> Load Images
                    </button>
                  </div>
                )}
                {showRemoteImages && (
                  <div className="flex items-center justify-between bg-green-500/10 border-b border-green-500/30 px-4 py-2">
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <ShieldOff className="w-4 h-4" />
                      <span>Remote images are loaded</span>
                    </div>
                    <button onClick={() => setShowRemoteImages(false)}
                      className="flex items-center gap-1.5 px-3 py-1 bg-slate-500/20 hover:bg-slate-500/30 text-slate-300 rounded text-xs font-medium transition-colors">
                      <ImageOff className="w-3 h-3" /> Block Images
                    </button>
                  </div>
                )}
                <iframe
                  srcDoc={buildHtmlSrcDoc(email.body)}
                  className="w-full min-h-[400px] border-0 bg-white"
                  style={{ height: 'auto' }}
                  title="Email content"
                  sandbox="allow-same-origin"
                  onLoad={(e) => {
                    const iframe = e.target;
                    if (iframe.contentDocument) {
                      const h = iframe.contentDocument.documentElement.scrollHeight || iframe.contentDocument.body.scrollHeight;
                      iframe.style.height = Math.max(h + 40, 400) + 'px';
                    }
                  }}
                />
              </>
            ) : (
              <div className="font-sans text-sm leading-relaxed p-6 bg-white">
                {(() => {
                  const segments = parseEmailSegments(email.body);
                  return segments.map((segment, idx) => {
                    if (segment.type === 'quoted') {
                      return <QuotedBlock key={idx} content={segment.content} linkifyText={linkifyText} />;
                    }
                    return (
                      <div key={idx} className="text-slate-700 whitespace-pre-wrap">
                        {linkifyText(segment.content)}
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>

          {/* Thread / Conversation View */}
          {email.thread_id && (
            <ThreadView threadId={email.thread_id} currentEmailId={emailId} />
          )}

          {/* Attachments */}
          {email.has_attachments && (
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <div className="flex items-center space-x-2 mb-3">
                <Paperclip className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-medium text-slate-300">
                  Attachments ({email.attachment_count || attachments.length})
                </span>
              </div>
              {loadingAttachments ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-500"></div>
                </div>
              ) : attachments.length > 0 ? (
                <div className="space-y-2">
                  {attachments.map((attachment) => (
                    <div key={attachment.id}
                      className="flex items-center justify-between bg-slate-700/50 rounded-lg p-3 hover:bg-slate-700 transition-colors group">
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400 flex-shrink-0">
                          {getFileIcon(attachment.content_type)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-white truncate" title={attachment.filename}>{attachment.filename}</p>
                          <p className="text-xs text-slate-400">
                            {formatFileSize(attachment.size_bytes)}
                            {attachment.content_type && ` \u2022 ${attachment.content_type.split('/')[1]?.toUpperCase()}`}
                          </p>
                        </div>
                      </div>
                      <button onClick={() => handleDownload(attachment)} disabled={downloadingId === attachment.id}
                        className="flex items-center space-x-1 px-3 py-1.5 bg-cyan-600/20 hover:bg-cyan-600/40 rounded-lg transition-colors text-cyan-400 text-sm opacity-0 group-hover:opacity-100 disabled:opacity-50">
                        {downloadingId === attachment.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-400"></div>
                        ) : (
                          <><Download className="w-4 h-4" /><span>Download</span></>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400">No attachments found</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const actionBar = email && !error && (
    <div className={`bg-slate-800/50 border-t border-slate-700 ${inline ? 'px-4 py-3' : 'px-6 py-4'} flex items-center justify-between flex-shrink-0`}>
      <div className="flex items-center space-x-2">
        <button onClick={() => onReply && onReply(email)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white text-sm font-medium">
          <Reply className="w-4 h-4" /><span>Reply</span>
        </button>
        <button onClick={() => onReplyAll && onReplyAll(email)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white text-sm font-medium">
          <ReplyAll className="w-4 h-4" /><span>Reply All</span>
        </button>
        <button onClick={() => onForward && onForward(email)}
          className="flex items-center space-x-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors text-white text-sm font-medium">
          <Forward className="w-4 h-4" /><span>Forward</span>
        </button>
        <button onClick={() => onFileToCase && onFileToCase(emailId)}
          className="flex items-center space-x-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 rounded-lg transition-colors text-white text-sm font-medium">
          <Folder className="w-4 h-4" /><span>File to Case</span>
        </button>
      </div>
      <button onClick={() => onDelete && onDelete(emailId)}
        className="flex items-center space-x-2 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-lg transition-colors text-red-400 text-sm font-medium">
        <Trash2 className="w-4 h-4" /><span>Delete</span>
      </button>
    </div>
  );

  // ---- Inline mode: no overlay, fills parent container ----
  const sidebarElement = email?.intelligence ? (
    <IntelligenceSidebar
      emailIntelligence={email.intelligence}
      emailId={emailId}
      onMatterLinked={onMatterLinked || (() => {})}
      onDeadlineAccepted={onDeadlineAccepted || (() => {})}
    />
  ) : null;

  if (inline) {
    return (
      <div className="h-full min-h-0 flex flex-col overflow-hidden bg-slate-900">
        {headerContent}
        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            {bodyContent}
            {actionBar}
          </div>
          {sidebarElement}
        </div>
      </div>
    );
  }

  // ---- Modal mode: fixed overlay (original behavior) ----
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-slate-700 flex flex-col">
        {headerContent}
        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden">
            {bodyContent}
            {actionBar}
          </div>
          {sidebarElement}
        </div>
      </div>
    </div>
  );
};

export default EmailViewModal;
